import moment from 'moment-timezone'

import { FEED_PUBLIC_FOR_ALL } from '../../Feeds/Utils/FeedsConstants'

export const OKR_CADENCE_WEEKLY = 'weekly'
export const OKR_CADENCE_DAILY = 'daily'
export const OKR_CADENCE_MONTHLY = 'monthly'
export const OKR_CADENCE_QUARTERLY = 'quarterly'

export const OKR_STATUS_ACTIVE = 'active'
export const OKR_STATUS_CLOSED = 'closed'

export const OKR_TYPE_MANUAL = 'manual'
export const OKR_TYPE_TIME_LOGGED_REVENUE = 'timeLoggedRevenue'

export const OKR_PACE_COMPLETED = 'completed'
export const OKR_PACE_AHEAD = 'ahead'
export const OKR_PACE_ON_TRACK = 'onTrack'
export const OKR_PACE_AT_RISK = 'atRisk'
export const OKR_PACE_OFF_TRACK = 'offTrack'
export const OKR_PACE_ENDED = 'ended'

export const OKR_CADENCES = [OKR_CADENCE_DAILY, OKR_CADENCE_WEEKLY, OKR_CADENCE_MONTHLY, OKR_CADENCE_QUARTERLY]
export const OKR_TYPES = [OKR_TYPE_MANUAL, OKR_TYPE_TIME_LOGGED_REVENUE]

export function normalizeOkrNumber(value, fallback = 0) {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

export function normalizeOkrTimezoneOffset(value) {
    if (value === undefined || value === null || value === '') return null

    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null
        return Math.abs(value) <= 16 ? value * 60 : value
    }

    if (typeof value !== 'string') return null

    const trimmedValue = value.trim()
    const numericValue = Number(trimmedValue)
    if (Number.isFinite(numericValue)) return normalizeOkrTimezoneOffset(numericValue)

    const offsetMatch = trimmedValue.match(/^(?:UTC|GMT)?([+-])(\d{1,2})(?::?(\d{2}))?$/i)
    if (!offsetMatch) return null

    const sign = offsetMatch[1] === '-' ? -1 : 1
    const hours = Number(offsetMatch[2])
    const minutes = Number(offsetMatch[3] || 0)
    return sign * (hours * 60 + minutes)
}

export function getOkrUserTimezone(user = {}) {
    const timezoneName = user.timezoneName || user.preferredTimezone || user.timeZone
    if (timezoneName && moment.tz.zone(timezoneName)) return timezoneName

    return user.timezone ?? user.timezoneOffset ?? user.timezoneMinutes ?? null
}

export function normalizeOkrType(type) {
    return OKR_TYPES.includes(type) ? type : OKR_TYPE_MANUAL
}

export function getOkrIsPublicFor(okr = {}) {
    if (Array.isArray(okr.isPublicFor) && okr.isPublicFor.length > 0) return okr.isPublicFor
    if (okr.isPrivate) return [okr.ownerId || okr.creatorId].filter(Boolean)
    return [FEED_PUBLIC_FOR_ALL]
}

export function isOkrPrivate(okr = {}) {
    return !getOkrIsPublicFor(okr).includes(FEED_PUBLIC_FOR_ALL)
}

export function canUserSeeOkr(okr = {}, userId) {
    const isPublicFor = getOkrIsPublicFor(okr)
    return (
        isPublicFor.includes(FEED_PUBLIC_FOR_ALL) ||
        okr.ownerId === userId ||
        okr.creatorId === userId ||
        isPublicFor.includes(userId)
    )
}

export function getOkrMoment(timestamp, timezone) {
    if (typeof timezone === 'string' && moment.tz.zone(timezone)) {
        return moment(timestamp).tz(timezone)
    }

    const timezoneOffset = normalizeOkrTimezoneOffset(timezone)
    return timezoneOffset !== null ? moment(timestamp).utcOffset(timezoneOffset) : moment(timestamp)
}

export function getOkrAllProjectsTodayKey(timestamp = Date.now(), timezone) {
    return getOkrMoment(timestamp, timezone).format('YYYY-MM-DD')
}

export function isRevenueOkr(okr) {
    return normalizeOkrType(okr?.type) === OKR_TYPE_TIME_LOGGED_REVENUE
}

export function calculateRevenueOkrCurrentValue(doneTimeMinutes, hourlyRate) {
    const minutes = normalizeOkrNumber(doneTimeMinutes)
    const rate = normalizeOkrNumber(hourlyRate)
    if (minutes <= 0 || rate <= 0) return 0
    return Number(((minutes / 60) * rate).toFixed(2))
}

export function resolveOkrCurrentValue(okr, revenueCurrentValue) {
    return isRevenueOkr(okr) ? normalizeOkrNumber(revenueCurrentValue) : normalizeOkrNumber(okr?.currentValue)
}

export function resolveOkrProgress(okr, revenueCurrentValue) {
    return calculateOkrProgress(resolveOkrCurrentValue(okr, revenueCurrentValue), okr?.targetValue)
}

export function calculateOkrProgress(currentValue, targetValue) {
    const current = normalizeOkrNumber(currentValue)
    const target = normalizeOkrNumber(targetValue)
    if (target <= 0) return 0
    return Math.max(0, Math.min(100, Math.round((current / target) * 100)))
}

export function clampOkrPercent(value) {
    const number = normalizeOkrNumber(value)
    return Math.max(0, Math.min(100, number))
}

export function calculateOkrPace(okr, now = Date.now()) {
    const actualPercent = calculateOkrProgress(resolveOkrCurrentValue(okr, okr?.resolvedCurrentValue), okr.targetValue)
    const periodStart = normalizeOkrNumber(okr.periodStart)
    const periodEnd = normalizeOkrNumber(okr.periodEnd)
    const duration = periodEnd - periodStart
    const expectedPercent =
        duration > 0 ? clampOkrPercent(((now - periodStart) / duration) * 100) : now >= periodEnd ? 100 : 0
    const roundedExpectedPercent = Math.round(expectedPercent)
    const delta = actualPercent - roundedExpectedPercent

    if (actualPercent >= 100) {
        return {
            actualPercent,
            expectedPercent: roundedExpectedPercent,
            delta,
            status: OKR_PACE_COMPLETED,
            textKey: 'OKR pace completed',
        }
    }

    if (now >= periodEnd) {
        return {
            actualPercent,
            expectedPercent: roundedExpectedPercent,
            delta,
            status: OKR_PACE_ENDED,
            textKey: 'OKR pace ended',
        }
    }

    if (delta >= 10) {
        return {
            actualPercent,
            expectedPercent: roundedExpectedPercent,
            delta,
            status: OKR_PACE_AHEAD,
            textKey: 'OKR pace ahead',
        }
    }

    if (delta >= -5) {
        return {
            actualPercent,
            expectedPercent: roundedExpectedPercent,
            delta,
            status: OKR_PACE_ON_TRACK,
            textKey: 'OKR pace on track',
        }
    }

    if (delta >= -20) {
        return {
            actualPercent,
            expectedPercent: roundedExpectedPercent,
            delta,
            status: OKR_PACE_AT_RISK,
            textKey: 'OKR pace at risk',
        }
    }

    return {
        actualPercent,
        expectedPercent: roundedExpectedPercent,
        delta,
        status: OKR_PACE_OFF_TRACK,
        textKey: 'OKR pace off track',
    }
}

export function getOkrPeriodForCadence(cadence, timestamp = Date.now()) {
    const date = moment(timestamp)

    if (cadence === OKR_CADENCE_DAILY) {
        return {
            periodStart: date.clone().startOf('day').valueOf(),
            periodEnd: date.clone().endOf('day').valueOf(),
        }
    }

    if (cadence === OKR_CADENCE_WEEKLY) {
        return {
            periodStart: date.clone().startOf('isoWeek').valueOf(),
            periodEnd: date.clone().endOf('isoWeek').valueOf(),
        }
    }

    if (cadence === OKR_CADENCE_QUARTERLY) {
        return {
            periodStart: date.clone().startOf('quarter').valueOf(),
            periodEnd: date.clone().endOf('quarter').valueOf(),
        }
    }

    return {
        periodStart: date.clone().startOf('month').valueOf(),
        periodEnd: date.clone().endOf('month').valueOf(),
    }
}

export function getOkrTimeLeftParts(periodEnd, now = Date.now()) {
    const remaining = normalizeOkrNumber(periodEnd) - now
    if (remaining <= 0) {
        return { textKey: 'OKR period ended', interpolations: {} }
    }

    const days = Math.ceil(remaining / 86400000)
    if (days > 1) {
        return { textKey: 'OKR days left', interpolations: { days } }
    }

    const hours = Math.max(1, Math.ceil(remaining / 3600000))
    return { textKey: 'OKR hours left', interpolations: { hours } }
}

export function formatOkrValue(value, unit) {
    const number = normalizeOkrNumber(value)
    const formattedNumber = number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })
    const cleanUnit = typeof unit === 'string' ? unit.trim() : ''
    return cleanUnit ? `${formattedNumber} ${cleanUnit}` : formattedNumber
}

export function formatOkrPeriodRange(periodStart, periodEnd) {
    const start = moment(normalizeOkrNumber(periodStart))
    const end = moment(normalizeOkrNumber(periodEnd))
    if (!start.isValid() || !end.isValid()) return ''
    const startFormat = start.year() === end.year() ? 'MMM D' : 'MMM D, YYYY'
    return `${start.format(startFormat)} – ${end.format('MMM D, YYYY')}`
}

// Must stay in sync with buildRecapChatId in functions/OKRs/okrRenewal.js so the
// client can deep-link a closed OKR back to its auto-generated recap chat topic.
export function buildOkrRecapChatId(projectId, ownerId, periodStart, periodEnd) {
    if (!projectId || !ownerId) return ''
    return `OKRRecap_${projectId}_${ownerId}_${periodStart}_${periodEnd}`.replace(/[^A-Za-z0-9_-]/g, '_')
}
